import React, { useContext, useRef } from "react";
import { endOfWeek, format, startOfWeek } from "date-fns";

import { COLOR_VALUES } from "shared/config/colors";
import { CalendarDate } from "@internationalized/date";
import SessionContext from "app/contexts/SessionContext";
import Table from "shared/components/data-display/tables/Table";
import VStack from "shared/components/layout/stack/VStack";
import WeekNavigatorContext from "shared/contexts/WeekNavigatorContext";
import useIncompleteTimesheetApi from "features/timesheet/incomplete-timesheet/useIncompleteTimesheet";
import useSmallBreakpoint from "shared/hooks/ui/useSmallBreakpoint";
import { useNavigate } from "react-router-dom";

const dateConverter = (date) => {
    const incompleteTimesheetDates = new Date(date);
    let startDate = format(
        startOfWeek(incompleteTimesheetDates, { weekStartsOn: 1 }),
        "MMM dd"
    );
    let endDate = format(
        endOfWeek(incompleteTimesheetDates, { weekStartsOn: 1 }),
        "MMM dd"
    );

    return `${startDate} - ${endDate}`;
};

const IncompleteTimesheet = () => {
    const session = useContext(SessionContext);
    const navigate = useNavigate();
    const isSmallScreen = useSmallBreakpoint();
    const { useIncompleteTimesheet } = useIncompleteTimesheetApi();
    const incompleteTimesheetData = useIncompleteTimesheet(session.legacyId);
    const { setPendingTimesheetCalendarDate } = useContext(WeekNavigatorContext);
    const yearWiesData = useRef();
    yearWiesData.current = [];

    if (!session.timesheetRequired) {
        globalThis.location.replace("/timesheets/");
    }

    if (incompleteTimesheetData.isLoading) {
        return <>Loading...</>;
    }

    if (incompleteTimesheetData.isError) {
        return <>Error</>;
    }

    const groupByYear = incompleteTimesheetData?.data.reduce((group, data) => {
        let date = new Date(data.date).getFullYear();
        group[date] = group[date] ?? [];
        group[date].push(data);
        return group;
    }, {});

    Object.entries(groupByYear).forEach(([key, value]) =>
        yearWiesData.current.push({ date: key, subRows: value })
    );

    const handleClick = (date) => {
        const newDateFormat = new Date(date);
        setPendingTimesheetCalendarDate(
            new CalendarDate(
                "AD",
                format(newDateFormat, "yyyy"),
                format(newDateFormat, "M"),
                format(newDateFormat, "d")
            )
        );

        navigate(
            `${globalThis.config.routes.timesheetLandingPage}` +
            `/${globalThis.config.routes.timesheetGrid}`
        );
    };

    const columns = [
        {
            headCellProps: {
                text: "Incomplete Timesheets",
                width: isSmallScreen ? "65%" : "85.37%",
            },

            getBodyCellProps: (cell) => ({
                text:
                    cell.value.length === 4
                        ? cell.value
                        : dateConverter(cell.value),
                actionIcon: cell.value.length !== 4 && "caret-right",
                padding: "10px 12px",
                actionProps: cell.value.length !== 4 && {
                    onClick: () => handleClick(cell.value),
                },
                color: "Black",
                variant: "Body_2_2",
                borderColor: cell.value.length === 4 && COLOR_VALUES.Gray_4,
            }),
            accessor: "date",
        },
        {
            headCellProps: {
                text: "Saved Hours",
                width: isSmallScreen ? "30%" : "12.2%",
            },
            getBodyCellProps: (cell) => {
                const fallbackText = cell.row.original.date.length === 4 ? cell.value : <>&#45;&#45;</>;
                return {
                    text: cell.value ? cell.value : fallbackText,
                    color: "Black",
                    padding: "10px 12px",
                    borderColor:
                        cell.row.original.date.length === 4 && COLOR_VALUES.Gray_4,
                };
            },
            accessor: "savedHours",
        },
    ];

    const sortBy = [
        {
            id: "date",
            desc: true,
        },
    ];

    return (
        <VStack>
            <Table
                showTableBorder={false}
                columns={columns}
                data={yearWiesData.current}
                sortBy={sortBy}
                pageSizeInitially={
                    yearWiesData.current?.length +
                    incompleteTimesheetData?.data?.length
                }
                displayPager={false}
                expandedAllRows={true}
            />
        </VStack>
    );
};

export default IncompleteTimesheet;
